// Brand capture (0.13) and the website studio (0.18-0.22, 1.32-1.33): the shapes the screens read (crm_brand_state,
// crm_brand_assets, crm_site, crm_site_version) and the small rules they apply before anything is sent. The database
// and the act function check everything again.
import type { JsonObject } from "./types";
import library from "./siteLibrary.json";
import { contrastRatio, INK, mix } from "./theme";
import type { SiteContext, SiteDocument, SiteSection } from "./siteRender";

export const SITE_LIBRARY = library as {
  version: string;
  sections: Record<string, { label: string; help: string; fields: LibraryField[] }>;
};

export interface LibraryField {
  key: string;
  label: string;
  kind: "text" | "textarea" | "image" | "toggle" | "list";
  max?: number;
  max_items?: number;
  item?: LibraryField[];
}

export const SECTION_TYPES = Object.keys(SITE_LIBRARY.sections);

// ---------------------------------------------------------------- brand
export interface BrandAsset {
  id: string;
  storage_path: string;
  kind: "logo" | "mark" | "photo" | "icon" | "pattern" | "document";
  subjects: string[];
  variant: string | null;
  source: string;
  content_type: string | null;
  width: number | null;
  height: number | null;
  alt: string | null;
  shows_people: boolean;
  rights_confirmed: boolean;
  people_consent: boolean;
  status: "suggested" | "approved" | "retired";
  usable: boolean;
  created_at: string;
}

export interface BrandCapture {
  id: string;
  status: "requested" | "proposed" | "waiting_for_playbook" | "approved" | "needs_input" | "failed";
  website: string | null;
  line: string | null;
  changes: string | null;
  uploads: string[];
  brand: JsonObject;
  report: JsonObject;
  config_version: number | null;
  created_at: string;
  updated_at: string;
}

export interface BrandState {
  capture: BrandCapture | null;
  assets: number;
  usable: number;
  needs_rights: number;
}

export const ASSET_SUBJECTS = ["team", "work", "premises", "product", "customer"] as const;
export const ASSET_KINDS = ["logo", "mark", "photo", "icon", "pattern", "document"] as const;
export const BRAND_FILE_TYPES: Record<string, string> = {
  svg: "image/svg+xml", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", pdf: "application/pdf",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};
export const MAX_BRAND_FILE = 20 * 1024 * 1024;
export const MAX_BRAND_FILES = 50;
export const BRAND_FOLDER = "brand/uploads";

/** Why these files cannot be uploaded (null when they can): SVG, PNG, JPG, WebP, PDF or PPTX, 20 MB each, 50 at most. */
export function checkBrandFiles(files: File[]): string | null {
  if (files.length > MAX_BRAND_FILES) return `Upload at most ${MAX_BRAND_FILES} files at a time.`;
  for (const f of files) {
    const ext = f.name.split(".").pop()?.toLowerCase() ?? "";
    if (!BRAND_FILE_TYPES[ext]) return `${f.name} is not a type we take. Use SVG, PNG, JPG, WebP, PDF or PPTX.`;
    if (f.size > MAX_BRAND_FILE) return `${f.name} is larger than 20 MB. Upload a smaller copy.`;
  }
  return null;
}

/** The five colour roles the CRM uses, in the order the owner sees them. */
export const COLOUR_ROLES: { key: string; label: string; hint: string }[] = [
  { key: "background", label: "Background", hint: "The page" },
  { key: "text", label: "Foreground", hint: "Body text" },
  { key: "primary", label: "Highlight", hint: "Your main colour" },
  { key: "action", label: "Action", hint: "Buttons" },
  { key: "reminder", label: "Reminder", hint: "Waiting on you" },
];

const HEX = /^#[0-9a-f]{6}$/i;
export const isHex = (v: unknown): v is string => typeof v === "string" && HEX.test(v);

/** Each role's contrast, against the background (text 4.5:1; highlight, action 3:1) or as a fill (reminder, ink 4.5:1). */
export function roleContrast(colours: JsonObject, role: string): { ratio: number; needed: number; ok: boolean } | null {
  const bg = isHex(colours.background) ? colours.background : "#ffffff";
  const c = colours[role];
  if (!isHex(c)) return null;
  if (role === "background") return null;
  const [ratio, needed] =
    role === "text" ? [contrastRatio(c, bg), 4.5] : role === "reminder" ? [contrastRatio(INK, c), 4.5] : [contrastRatio(c, bg), 3];
  return { ratio: Math.round(ratio * 100) / 100, needed, ok: ratio >= needed };
}

// ---------------------------------------------------------------- website
export interface SiteInfo {
  id: string;
  status: "draft" | "live";
  pages_project: string;
  preview_url: string | null;
  domain: string | null;
  domain_status: "none" | "requested" | "pending" | "active" | "error";
  domain_records: { type: string; name: string; value: string; note?: string }[];
  domain_detail: string | null;
  domain_checked_at: string | null;
  site_key: string | null;
  live_version: number | null;
  brief: SiteBrief | JsonObject;
}

export type VersionSource = "draft" | "import" | "chat" | "click" | "agent" | "restore";
export type VersionStatus = "draft" | "approved" | "publishing" | "live" | "archived" | "rejected" | "failed";

export interface SiteVersionRow {
  version: number;
  source: VersionSource;
  status: VersionStatus;
  based_on: number | null;
  note: string | null;
  placeholders: number;
  flags: JsonObject[];
  created_by: string;
  created_at: string;
  approved_by?: string | null;
  published_at?: string | null;
  deploy?: JsonObject;
}

export interface SiteVersion extends SiteVersionRow {
  document: SiteDocument;
}

export interface SiteRequest {
  id: string;
  kind: "draft_site" | "import_site" | "rewrite_site_section" | "deploy_site" | "connect_domain";
  status: string;
  payload: JsonObject;
  created_at: string;
}

export interface SiteState {
  site: SiteInfo | null;
  versions: SiteVersionRow[];
  pages?: { slug: string; title: string; description: string | null }[];
  requests: SiteRequest[];
}

export interface SiteBrief {
  description: string;
  pages?: { name: string; sections?: string; wording?: string }[];
  services?: string[];
  tone?: string;
  likes?: { url: string; why?: string }[];
  words_to_use?: string[];
  words_to_avoid?: string[];
  main_action?: string;
  notes?: string;
}

export const MAIN_ACTIONS: { value: string; label: string }[] = [
  { value: "enquire", label: "Send an enquiry" },
  { value: "call", label: "Call us" },
  { value: "book", label: "Book a time (after an enquiry)" },
  { value: "buy", label: "Buy" },
  { value: "visit", label: "Visit us" },
  { value: "subscribe", label: "Sign up to the newsletter" },
];

export const SOURCE_LABEL: Record<VersionSource, string> = {
  draft: "Ogma's draft", import: "Imported", chat: "Ogma, as you asked", click: "Your edit", agent: "Ogma's proposal", restore: "Restored",
};
export const STATUS_LABEL: Record<VersionStatus, string> = {
  draft: "Draft", approved: "Approved", publishing: "Publishing…", live: "Live", archived: "Earlier", rejected: "Rejected", failed: "Publish failed",
};

/** Made by Ogma (needs the owner's approval, shown as a before and after) rather than the owner's own edit. */
export const byOgma = (v: Pick<SiteVersionRow, "source">) => v.source !== "click" && v.source !== "restore";

/** The version to open: the newest draft if there is one, else the live one, else the newest. */
export function versionToOpen(versions: SiteVersionRow[]): SiteVersionRow | null {
  const sorted = [...versions].sort((a, b) => b.version - a.version);
  const live = sorted.find((v) => v.status === "live");
  const draft = sorted.find((v) => v.status === "draft" || v.status === "approved" || v.status === "failed");
  if (draft && (!live || draft.version > live.version)) return draft;
  return live ?? sorted[0] ?? null;
}

/** Pending work Ogma is doing for the studio, in words. */
export function requestLabel(r: SiteRequest): string {
  return {
    draft_site: "Ogma is drafting your site from your brief",
    import_site: "Ogma is importing your existing site",
    rewrite_site_section: "Ogma is rewriting a section as you asked",
    deploy_site: "Your site is being published",
    connect_domain: "Your domain is being connected",
  }[r.kind];
}

/** A new section of a type from the library, with empty fields. */
export function newSection(type: string): Pick<SiteSection, "type" | "fields"> {
  if (!SITE_LIBRARY.sections[type]) throw new Error(`unknown section type ${type}`);
  return { type, fields: type === "contact" ? { heading: "Get in touch" } : {} };
}

/** The sections whose fields differ between two versions of one page: for the before and after. */
export function changedSections(before: SiteDocument | null, after: SiteDocument): { page: string; id: string; type: string; change: "added" | "removed" | "changed" }[] {
  const out: { page: string; id: string; type: string; change: "added" | "removed" | "changed" }[] = [];
  for (const p of after.pages) {
    const old = before?.pages.find((x) => x.slug === p.slug);
    for (const s of p.sections) {
      const o = old?.sections.find((x) => x.id === s.id);
      if (!o) out.push({ page: p.slug, id: s.id, type: s.type, change: "added" });
      else if (JSON.stringify(o.fields) !== JSON.stringify(s.fields) || o.type !== s.type) out.push({ page: p.slug, id: s.id, type: s.type, change: "changed" });
    }
    for (const o of old?.sections ?? []) if (!p.sections.some((x) => x.id === o.id)) out.push({ page: p.slug, id: o.id, type: o.type, change: "removed" });
  }
  return out;
}

/**
 * The preview's context from the approved settings, as engine/site_builder.py site_context builds it for the real
 * site: brand colours (contrast kept), fonts with fallbacks, the logo, and the business's call to action. The form and
 * newsletter show as notes in the preview; the published site carries the intake snippet.
 */
export function previewContext(config: JsonObject | null | undefined, opts: { assets?: SiteContext["assets"]; logoUrl?: string | null; cta?: SiteContext["cta"] } = {}): SiteContext {
  const brand = (config?.brand ?? {}) as JsonObject;
  const c = (brand.colours ?? {}) as JsonObject;
  const tenant = (config?.tenant ?? {}) as JsonObject;
  const low = (v: unknown) => (typeof v === "string" ? v.toLowerCase() : "");
  const bg = isHex(c.background) ? low(c.background) : "#ffffff";
  let text = isHex(c.text) ? low(c.text) : "#1b1f23";
  if (contrastRatio(text, bg) < 4.5) text = contrastRatio("#1b1f23", bg) >= 4.5 ? "#1b1f23" : "#000000";
  let primary = isHex(c.primary) ? low(c.primary) : text;
  if (contrastRatio(primary, bg) < 3) primary = text;
  let action = isHex(c.action) ? low(c.action) : primary;
  const on = (x: string) => (contrastRatio("#ffffff", x) >= contrastRatio(INK, x) ? "#ffffff" : INK);
  if (contrastRatio(on(action), action) < 4.5) action = contrastRatio(on(primary), primary) >= 4.5 ? primary : text;
  const fonts = (brand.fonts ?? {}) as JsonObject;
  const ok = (n: unknown): n is string => typeof n === "string" && /^[A-Za-z0-9 ]{2,40}$/.test(n);
  const fbH = typeof fonts.fallback_headings === "string" ? fonts.fallback_headings : "Georgia, serif";
  const fbB = typeof fonts.fallback_body === "string" ? fonts.fallback_body : "Arial, sans-serif";
  const legal = [tenant.legal_name || tenant.name, tenant.company_number ? `company number ${String(tenant.company_number)}` : null].filter(Boolean).join(", ");
  return {
    name: typeof tenant.name === "string" ? tenant.name : "",
    lang: "en-GB",
    colours: { bg, text, primary, action, on_action: on(action), soft: mix(primary, 6, bg), line: mix(text, 15, bg) },
    fonts: { head: ok(fonts.headings) ? `'${fonts.headings}', ${fbH}` : fbH, body: ok(fonts.body) ? `'${fonts.body}', ${fbB}` : fbB, google: [...new Set([fonts.headings, fonts.body].filter(ok))] },
    logo: opts.logoUrl ? { url: opts.logoUrl, alt: String(tenant.name ?? "") } : null,
    cta: opts.cta ?? { label: "Get in touch", href: "#contact", heading: "Ready when you are" },
    snippet: null,
    assets: opts.assets ?? {},
    legal: String(legal),
    articles: [],
    base: "/",
    url: "",
  };
}

/** What the owner does with each DNS record, in words. */
export function domainStatusText(site: Pick<SiteInfo, "domain" | "domain_status" | "domain_detail">): string {
  switch (site.domain_status) {
    case "requested":
      return `Connecting ${site.domain}. The records to add appear here shortly.`;
    case "pending":
      return `Add the records below where you bought ${site.domain}. Ogma checks them every day until the site answers there over HTTPS.`;
    case "active":
      return `Live at https://${site.domain}.`;
    case "error":
      return `${site.domain}: ${site.domain_detail ?? "something went wrong"}. Check the records, or connect it again.`;
    default:
      return "No domain yet. Your site is on its preview address until you connect one you own.";
  }
}
